import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { httpRemoveNeighborhoodsFromSubgeo } from '@/features/subgeo/http/remove-neighborhoods-from-subgeo.http.ts'
import { useSubgeoStore } from '@/features/subgeo/store/subgeo-store.ts'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS } from '@/query-client'
import { useTranslation } from '@/translations/i18n'

interface UseRemoveNeighborhoodPayload {
	neighborhoodId: string
	currentSubgeoId: string
}

export const useRemoveNeighborhood = () => {
	const queryClient = useQueryClient()
	const { t } = useTranslation()

	return useMutation({
		mutationFn: ({
			neighborhoodId,
			currentSubgeoId
		}: UseRemoveNeighborhoodPayload) => {
			return httpRemoveNeighborhoodsFromSubgeo({
				urlParams: { id: currentSubgeoId },
				payload: { neighborhoodIds: [neighborhoodId] },
				params: { responseType: 'extended' }
			})
		},
		onMutate: ({ neighborhoodId, currentSubgeoId }) => {
			const currentSubgeos = useSubgeoStore.getState().subgeos

			const updatedSubgeos = currentSubgeos.map((subgeo) => {
				if (subgeo.id === currentSubgeoId) {
					return {
						...subgeo,
						neighborhoodIds: subgeo.neighborhoodIds.filter(
							(id) => id !== neighborhoodId
						)
					}
				}
				return subgeo
			})

			useSubgeoStore.getState().setSubgeos(updatedSubgeos)

			return { previousSubgeos: currentSubgeos }
		},
		onError: (error, _variables, context) => {
			if (context?.previousSubgeos) {
				useSubgeoStore.getState().setSubgeos(context.previousSubgeos)
			}
			errorHandler(error)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_SUBGEOS]
			})
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_NEIGHBORHOODS]
			})
			toast.success(t('subgeo:remove_neighborhood_success'))
		}
	})
}
